import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const ROOT = window.Shopify?.routes?.root || '/';
const B2B_QUICK_SELECTOR = '.product-card__b2b-quick[data-variant-id]';
const B2B_INPUT_SELECTOR = '.b2b-qty-live__input';
const B2B_STATUS_SELECTOR = '[data-b2b-cart-status]';
const B2B_STICKY_SELECTOR = '[data-b2b-cart-sticky]';
const SYNC_DEBOUNCE_MS = 400;
const STATUS_MESSAGE_MS = 1300;
const RECENT_INTERACTION_MS = 2000;
const CART_PULSE_MS = 520;

const state = {
  cart: null,
  cartPromise: null,
  hydrateTimer: null,
  initialized: false,
  variantStates: new Map(),
};

function getVariantState(variantId) {
  const key = String(variantId);

  if (!state.variantStates.has(key)) {
    state.variantStates.set(key, {
      confirmedQty: 0,
      displayQty: 0,
      desiredQty: 0,
      inflight: false,
      syncTimer: null,
      lastMessageTimer: null,
      lastInteractionAt: 0,
    });
  }

  return state.variantStates.get(key);
}

function getQuickElementsByVariantId(variantId) {
  return document.querySelectorAll(
    `${B2B_QUICK_SELECTOR}[data-variant-id="${CSS.escape(String(variantId))}"]`
  );
}

function getQuickElement(variantId) {
  return document.querySelector(
    `${B2B_QUICK_SELECTOR}[data-variant-id="${CSS.escape(String(variantId))}"]`
  );
}

function getStickySummary() {
  return document.querySelector(B2B_STICKY_SELECTOR);
}

function hasB2BQuickControls() {
  return document.querySelector(B2B_QUICK_SELECTOR) !== null;
}

function isB2BQuickControlNode(node) {
  if (!(node instanceof Element)) return false;
  return node.matches(B2B_QUICK_SELECTOR) || node.querySelector(B2B_QUICK_SELECTOR) !== null;
}

function isVariantAvailable(variantId) {
  const quick = getQuickElement(variantId);
  if (!quick) return true;

  return String(quick.dataset.available || '').toLowerCase() !== 'false';
}

function getVariantMaxQty(variantId) {
  const quick = getQuickElement(variantId);
  if (!quick) return 9999;

  const rawMax = Number.parseInt(quick.dataset.maxQty || '', 10);

  if (!isVariantAvailable(variantId)) return 0;
  if (Number.isNaN(rawMax)) return 9999;

  return Math.max(0, rawMax);
}

function clampQty(variantId, quantity) {
  const parsedQty = Math.max(0, Number.parseInt(quantity, 10) || 0);
  return Math.min(parsedQty, getVariantMaxQty(variantId));
}

function getCartQty(cart, variantId) {
  if (!cart?.items) return 0;

  const item = cart.items.find((cartItem) => String(cartItem.variant_id) === String(variantId));
  return item ? item.quantity : 0;
}

function collectSectionIds() {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      sectionIds.add(element.dataset.sectionId);
    }
  });

  return Array.from(sectionIds);
}

function getSummaryData(cart) {
  if (!cart?.items?.length) {
    return {
      skuCount: 0,
      totalUnits: 0,
      subtotalAmount: null,
    };
  }

  const skuCount = cart.items.filter((item) => Number(item.quantity) > 0).length;
  const totalUnits = cart.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const subtotalAmount = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : null;

  return {
    skuCount,
    totalUnits,
    subtotalAmount,
  };
}

function formatChileanMoney(amount) {
  if (typeof amount !== 'number') return '';

  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

function renderStickySummary(cart) {
  const sticky = getStickySummary();
  if (!(sticky instanceof HTMLElement)) return;

  const summary = getSummaryData(cart);
  const skus = sticky.querySelector('[data-b2b-cart-skus]');
  const units = sticky.querySelector('[data-b2b-cart-units]');
  const subtotal = sticky.querySelector('[data-b2b-cart-subtotal]');

  sticky.hidden = summary.totalUnits <= 0;

  if (skus instanceof HTMLElement) {
    skus.textContent = `${summary.skuCount} ${summary.skuCount === 1 ? 'SKU' : 'SKUs'}`;
  }

  if (units instanceof HTMLElement) {
    units.textContent = `${summary.totalUnits} ${summary.totalUnits === 1 ? 'unidad' : 'unidades'}`;
  }

  if (subtotal instanceof HTMLElement) {
    if (summary.subtotalAmount !== null) {
      subtotal.hidden = false;
      subtotal.textContent = `${formatChileanMoney(Math.round(summary.subtotalAmount / 1.19))} + IVA`;
    } else {
      subtotal.hidden = true;
      subtotal.textContent = '';
    }
  }
}

function pulseCartIcon() {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    if (!(icon instanceof HTMLElement)) return;

    icon.classList.remove('b2b-cart-pulse');
    window.requestAnimationFrame(() => {
      icon.classList.add('b2b-cart-pulse');
      window.setTimeout(() => {
        icon.classList.remove('b2b-cart-pulse');
      }, CART_PULSE_MS);
    });
  });
}

async function fetchCart(force = false) {
  if (!force && state.cart) return state.cart;
  if (!force && state.cartPromise) return state.cartPromise;

  state.cartPromise = fetch(`${ROOT}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error('No se pudo cargar el carrito B2B.');
      }

      const cart = await response.json();
      state.cart = cart;
      return cart;
    })
    .finally(() => {
      state.cartPromise = null;
    });

  return state.cartPromise;
}

function dispatchThemeCartUpdate(cart, sections = {}) {
  const event = new CartUpdateEvent(cart || {}, 'b2b-cart-controller', {
    itemCount: cart?.item_count || 0,
    source: 'b2b-cart-controller',
    sections,
  });

  document.dispatchEvent(event);
}

function setStatus(quick, message, tone = 'idle', persist = false) {
  const status = quick.querySelector(B2B_STATUS_SELECTOR);
  if (!(status instanceof HTMLElement)) return;

  status.textContent = message || '';
  status.dataset.state = tone;

  const variantId = quick.dataset.variantId;
  if (!variantId) return;

  const variantState = getVariantState(variantId);
  if (variantState.lastMessageTimer) {
    window.clearTimeout(variantState.lastMessageTimer);
    variantState.lastMessageTimer = null;
  }

  if (!persist && message) {
    variantState.lastMessageTimer = window.setTimeout(() => {
      status.textContent = '';
      status.dataset.state = 'idle';
      variantState.lastMessageTimer = null;
    }, STATUS_MESSAGE_MS);
  }
}

function clearVariantSyncTimer(variantState) {
  if (!variantState.syncTimer) return;

  window.clearTimeout(variantState.syncTimer);
  variantState.syncTimer = null;
}

function markVariantInteraction(variantState) {
  variantState.lastInteractionAt = Date.now();
}

function hasRecentInteraction(variantState) {
  return Date.now() - variantState.lastInteractionAt < RECENT_INTERACTION_MS;
}

function hasPendingSync(variantState) {
  return variantState.inflight || variantState.syncTimer !== null || variantState.desiredQty !== variantState.confirmedQty;
}

function setVisibleQty(variantId, quantity) {
  const safeQty = clampQty(variantId, quantity);

  getQuickElementsByVariantId(variantId).forEach((quick) => {
    const input = quick.querySelector(B2B_INPUT_SELECTOR);
    if (input instanceof HTMLInputElement) {
      input.value = String(safeQty);
      input.max = String(getVariantMaxQty(variantId));
    }
  });
}

function updateQuickUI(variantId, options = {}) {
  const variantState = getVariantState(variantId);
  const safeQty = clampQty(variantId, variantState.displayQty);
  const maxQty = getVariantMaxQty(variantId);
  const soldOut = maxQty <= 0 || !isVariantAvailable(variantId);

  getQuickElementsByVariantId(variantId).forEach((quick) => {
    const control = quick.querySelector('.b2b-qty-live');
    const plus = quick.querySelector('.b2b-qty-live__plus');
    const minus = quick.querySelector('.b2b-qty-live__minus');
    const input = quick.querySelector(B2B_INPUT_SELECTOR);

    quick.classList.toggle('is-sold-out', soldOut);
    quick.toggleAttribute('data-b2b-updating', variantState.inflight);

    if (control instanceof HTMLElement) {
      control.setAttribute('aria-busy', String(variantState.inflight));
    }

    if (input instanceof HTMLInputElement) {
      input.disabled = soldOut;
      input.max = String(maxQty);
      input.value = String(safeQty);
    }

    if (plus instanceof HTMLButtonElement) {
      const disabled = soldOut || safeQty >= maxQty;
      plus.disabled = disabled;
      plus.setAttribute('aria-disabled', String(disabled));
    }

    if (minus instanceof HTMLButtonElement) {
      const disabled = safeQty <= 0;
      minus.disabled = disabled;
      minus.setAttribute('aria-disabled', String(disabled));
    }

    if (options.message) {
      setStatus(quick, options.message, options.tone, options.persist);
    }
  });
}

function syncVisibleCardsFromCart(cart, options = {}) {
  const { resetLocalState = false } = options;

  document.querySelectorAll(B2B_QUICK_SELECTOR).forEach((quick) => {
    const variantId = quick.dataset.variantId;
    if (!variantId) return;

    const variantState = getVariantState(variantId);
    const confirmedQty = clampQty(variantId, getCartQty(cart, variantId));
    const preserveDisplay = !resetLocalState && hasRecentInteraction(variantState) && hasPendingSync(variantState);

    variantState.confirmedQty = confirmedQty;

    if (resetLocalState) {
      variantState.displayQty = confirmedQty;
      variantState.desiredQty = confirmedQty;
      clearVariantSyncTimer(variantState);
      variantState.inflight = false;
    } else if (!preserveDisplay) {
      variantState.displayQty = confirmedQty;

      if (!hasPendingSync(variantState)) {
        variantState.desiredQty = confirmedQty;
      }
    }

    setVisibleQty(variantId, variantState.displayQty);
    updateQuickUI(variantId);
  });
}

function syncAllVariantStatesFromCart(cart) {
  state.variantStates.forEach((variantState, variantId) => {
    const confirmedQty = clampQty(variantId, getCartQty(cart, variantId));
    variantState.confirmedQty = confirmedQty;
    variantState.displayQty = confirmedQty;
    variantState.desiredQty = confirmedQty;
    variantState.inflight = false;
    clearVariantSyncTimer(variantState);
  });
}

async function updateVariantQuantity(variantId, quantity) {
  const sectionIds = collectSectionIds();
  const payload = {
    updates: {
      [String(variantId)]: quantity,
    },
  };

  if (sectionIds.length > 0) {
    payload.sections = sectionIds;
    payload.sections_url = window.location.pathname;
  }

  const response = await fetch(`${ROOT}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('No se pudo actualizar el carrito B2B.');
  }

  return response.json();
}

async function clearCart() {
  const response = await fetch(`${ROOT}cart/clear.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error('No se pudo vaciar el carrito B2B.');
  }

  return response.json();
}

async function processVariantQueue(variantId) {
  const variantState = getVariantState(variantId);
  if (variantState.inflight) return;

  clearVariantSyncTimer(variantState);
  variantState.inflight = true;

  try {
    while (variantState.desiredQty !== variantState.confirmedQty) {
      const quantityToSync = clampQty(variantId, variantState.desiredQty);
      const cart = await updateVariantQuantity(variantId, quantityToSync);
      state.cart = cart;

      const confirmedQty = clampQty(variantId, getCartQty(cart, variantId));
      variantState.confirmedQty = confirmedQty;
      if (variantState.desiredQty === confirmedQty) {
        variantState.displayQty = confirmedQty;
      }

      syncVisibleCardsFromCart(cart);
      renderStickySummary(cart);
      dispatchThemeCartUpdate(cart, cart.sections || {});

      if (variantState.desiredQty === confirmedQty) {
        updateQuickUI(variantId, {
          message: 'Guardado',
          tone: 'success',
        });
        pulseCartIcon();
      }
    }
  } catch (error) {
    console.error(error);

    const freshCart = await fetchCart(true).catch(() => null);
    if (freshCart) {
      syncVisibleCardsFromCart(freshCart, { resetLocalState: true });
      renderStickySummary(freshCart);
      dispatchThemeCartUpdate(freshCart, freshCart.sections || {});
    }

    getQuickElementsByVariantId(variantId).forEach((quick) => {
      setStatus(quick, 'Error', 'error');
    });
  } finally {
    variantState.inflight = false;
    updateQuickUI(variantId);

    if (variantState.desiredQty !== variantState.confirmedQty) {
      processVariantQueue(variantId);
    }
  }
}

function queueVariantQuantity(variantId, nextQty) {
  const variantState = getVariantState(variantId);
  const safeQty = clampQty(variantId, nextQty);
  markVariantInteraction(variantState);
  variantState.displayQty = safeQty;
  variantState.desiredQty = safeQty;

  setVisibleQty(variantId, variantState.displayQty);
  updateQuickUI(variantId, {
    message: 'Pendiente...',
    tone: 'pending',
  });

  clearVariantSyncTimer(variantState);
  variantState.syncTimer = window.setTimeout(() => {
    variantState.syncTimer = null;
    processVariantQueue(variantId);
  }, SYNC_DEBOUNCE_MS);
}

function stopCardClick(event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
}

function handleButtonClick(event) {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('.b2b-qty-live__plus, .b2b-qty-live__minus');
  if (!(button instanceof HTMLButtonElement)) return;

  stopCardClick(event);
  if (button.disabled) return;

  const quick = button.closest(B2B_QUICK_SELECTOR);
  if (!(quick instanceof HTMLElement) || !quick.dataset.variantId) return;

  const variantId = quick.dataset.variantId;
  const variantState = getVariantState(variantId);
  const baseQty = variantState.displayQty;
  const delta = button.classList.contains('b2b-qty-live__plus') ? 1 : -1;

  queueVariantQuantity(variantId, baseQty + delta);
}

function sanitizeInputValue(input) {
  const raw = input.value.replace(/[^\d]/g, '');
  input.value = raw === '' ? '0' : raw;
}

function handleInput(event) {
  if (!(event.target instanceof Element)) return;
  const input = event.target.closest(B2B_INPUT_SELECTOR);
  if (!(input instanceof HTMLInputElement)) return;

  sanitizeInputValue(input);

  const quick = input.closest(B2B_QUICK_SELECTOR);
  if (!(quick instanceof HTMLElement) || !quick.dataset.variantId) return;

  const variantId = quick.dataset.variantId;
  const nextQty = clampQty(variantId, input.value);
  const variantState = getVariantState(variantId);

  markVariantInteraction(variantState);
  variantState.displayQty = nextQty;
  variantState.desiredQty = nextQty;
  setVisibleQty(variantId, nextQty);
  updateQuickUI(variantId, {
    message: 'Pendiente...',
    tone: 'pending',
  });
}

function handleBlur(event) {
  if (!(event.target instanceof Element)) return;
  const input = event.target.closest(B2B_INPUT_SELECTOR);
  if (!(input instanceof HTMLInputElement)) return;

  const quick = input.closest(B2B_QUICK_SELECTOR);
  if (!(quick instanceof HTMLElement) || !quick.dataset.variantId) return;

  const variantId = quick.dataset.variantId;
  const nextQty = clampQty(variantId, input.value);

  queueVariantQuantity(variantId, nextQty);
}

function handleNoLinkGuard(event) {
  if (!(event.target instanceof Element)) return;
  const b2bInfo = event.target.closest('[data-b2b-no-link]');
  if (!b2bInfo) return;

  const interactive = event.target.closest('input, button, select, textarea, label');
  if (interactive) return;

  stopCardClick(event);
}

async function hydrateVisibleCards(force = false) {
  if (!hasB2BQuickControls() && !(getStickySummary() instanceof HTMLElement)) return;

  try {
    const cart = await fetchCart(force);
    syncVisibleCardsFromCart(cart);
    renderStickySummary(cart);
  } catch (error) {
    console.error(error);
  }
}

function scheduleHydrate(force = false) {
  window.clearTimeout(state.hydrateTimer);
  state.hydrateTimer = window.setTimeout(() => {
    hydrateVisibleCards(force);
  }, 120);
}

function openCartReview() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer instanceof HTMLElement && typeof drawer.open === 'function') {
    drawer.open();
    return;
  }

  const sticky = getStickySummary();
  const cartUrl = sticky instanceof HTMLElement ? sticky.dataset.cartUrl : `${ROOT}cart`;
  window.location.href = cartUrl || `${ROOT}cart`;
}

async function handleStickyAction(event) {
  if (!(event.target instanceof Element)) return;

  const reviewButton = event.target.closest('[data-b2b-cart-review]');
  if (reviewButton instanceof HTMLButtonElement) {
    event.preventDefault();
    openCartReview();
    return;
  }

  const clearButton = event.target.closest('[data-b2b-cart-clear]');
  if (!(clearButton instanceof HTMLButtonElement)) return;

  event.preventDefault();

  if (!window.confirm('¿Quieres vaciar tu pedido mayorista?')) {
    return;
  }

  clearButton.disabled = true;

  try {
    const cart = await clearCart();
    state.cart = cart;
    syncAllVariantStatesFromCart(cart);
    syncVisibleCardsFromCart(cart, { resetLocalState: true });
    renderStickySummary(cart);
    dispatchThemeCartUpdate(cart, cart.sections || {});
  } catch (error) {
    console.error(error);
  } finally {
    clearButton.disabled = false;
  }
}

function bindEvents() {
  document.addEventListener('click', handleButtonClick, true);
  document.addEventListener('click', handleNoLinkGuard, true);
  document.addEventListener('click', handleStickyAction, true);
  document.addEventListener('input', handleInput, true);
  document.addEventListener('blur', handleBlur, true);

  document.addEventListener('shopify:section:load', () => {
    scheduleHydrate(true);
  });

  document.addEventListener('shopify:section:reorder', () => {
    scheduleHydrate(true);
  });

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.sourceId === 'b2b-cart-controller') return;
    scheduleHydrate(true);
  });

  const observer = new MutationObserver((mutations) => {
    const shouldHydrate = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => isB2BQuickControlNode(node))
    );

    if (shouldHydrate) {
      scheduleHydrate(true);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function init() {
  if (state.initialized) return;

  state.initialized = true;
  bindEvents();
  if (hasB2BQuickControls() || getStickySummary() instanceof HTMLElement) {
    hydrateVisibleCards(true);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
